import React, { useEffect, useState } from "react";
import api, { formatTL, formatDateTime, formatDuration } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plate } from "@/components/Plate";
import { BADGE_COLORS } from "@/components/BusinessTag";
import { Buildings, Trash, Plus, Storefront, Receipt, HandCoins } from "@phosphor-icons/react";
import BusinessReceivablesDialog from "@/components/BusinessReceivablesDialog";

const TYPE_LABELS = {
  plates: "Plaka Listesi",
  quota: "Kontenjan",
  first_hours: "İlk Saatler İşletmeden",
};

const COLOR_LABELS = { violet: "Mor", orange: "Turuncu", sky: "Mavi", rose: "Pembe", teal: "Turkuaz", lime: "Yeşil" };

const empty = {
  name: "", contact_email: "", contact_phone: "", billing_email: "",
  discount_percent: 0, monthly_credit_limit: 0, plates: "", notes: "",
  agreement_type: "plates", quota_slots: 3, covered_hours: 1, business_fee_per_vehicle: 100, monthly_fee: 0,
  customer_deduction: "agreement", badge_color: "violet", active: true,
};

export default function CorporateAccounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [stmtFor, setStmtFor] = useState(null);
  const [recvFor, setRecvFor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/corporate-accounts");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      discount_percent: Number(form.discount_percent),
      monthly_credit_limit: Number(form.monthly_credit_limit),
      quota_slots: Number(form.quota_slots) || 0,
      covered_hours: Number(form.covered_hours) || 1,
      business_fee_per_vehicle: Number(form.business_fee_per_vehicle) || 0,
      monthly_fee: Number(form.monthly_fee) || 0,
      plates: form.plates.split(/[\n,]/).map((p) => p.trim().toUpperCase()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/corporate-accounts/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/corporate-accounts", payload);
        toast.success("Oluşturuldu");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...empty,
      ...it,
      plates: (it.plates || []).join(", "),
      notes: it.notes || "",
      contact_email: it.contact_email || "",
      contact_phone: it.contact_phone || "",
      billing_email: it.billing_email || "",
      agreement_type: it.agreement_type || "plates",
      active: it.active !== false,
    });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Kurumsal hesap silinsin mi?")) return;
    try { await api.delete(`/corporate-accounts/${id}`); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPlates = form.agreement_type === "plates";
  const isQuota = form.agreement_type === "quota";
  const isFirst = form.agreement_type === "first_hours";

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">B2B</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Kurumsal Hesaplar & İşletme Anlaşmaları</h1>
          <p className="text-sm text-muted-foreground mt-1">Sabit plaka listesi, kontenjanlı muafiyet veya "ilk saatler işletmeden" anlaşmaları — hepsi buradan, kod gerekmez.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="corp-add-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              <Plus size={16} /> Yeni Hesap
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Hesap Düzenle" : "Yeni Kurumsal Hesap / İşletme"}</DialogTitle>
              <DialogDescription>Anlaşma tipini seçin; alanlar tipe göre değişir.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div><Label>İşletme / Şirket Adı</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" data-testid="corp-name-input" /></div>

              <div>
                <Label>Anlaşma Tipi</Label>
                <Select value={form.agreement_type} onValueChange={(v) => set("agreement_type", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="corp-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plates">Plaka Listesi — belirli plakalar sürekli muaf/indirimli</SelectItem>
                    <SelectItem value="quota">Kontenjan — aynı anda en fazla N araç muaf</SelectItem>
                    <SelectItem value="first_hours">İlk Saatler İşletmeden — ilk X saat işletme öder</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isQuota && (
                <div className="p-3 rounded-sm border border-violet-200 bg-violet-50/50 space-y-2">
                  <div>
                    <Label>Kontenjan (aynı anda araç)</Label>
                    <Input type="number" min="1" value={form.quota_slots} onChange={(e) => set("quota_slots", e.target.value)} className="rounded-sm font-mono" data-testid="corp-quota-input" />
                  </div>
                  <div>
                    <Label>Aylık Abonelik Bedeli (₺)</Label>
                    <Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={(e) => set("monthly_fee", e.target.value)} className="rounded-sm font-mono" data-testid="corp-monthly-fee-input" />
                    <div className="text-[10px] text-muted-foreground mt-1">İşletmeden alınan aylık sabit bedel — Abonelik Ödemeleri sayfasında "Ödeme Al" ile tahsil edilir.</div>
                  </div>
                  <div className="text-[11px] text-violet-800">Kontenjan içindeki araçlar tamamen muaf. Kontenjan dolunca gelen araca <b>otomatik normal tarife</b> uygulanır (operatöre bilgi rozeti gösterilir).</div>
                </div>
              )}

              {isFirst && (
                <div className="p-3 rounded-sm border border-orange-200 bg-orange-50/50 space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Kapsanan Saat</Label>
                      <Input type="number" min="0.5" step="0.5" value={form.covered_hours} onChange={(e) => set("covered_hours", e.target.value)} className="rounded-sm font-mono" data-testid="corp-covered-hours-input" />
                    </div>
                    <div>
                      <Label>İşletmeye Yazılacak (₺/araç)</Label>
                      <Input type="number" min="0" step="0.01" value={form.business_fee_per_vehicle} onChange={(e) => set("business_fee_per_vehicle", e.target.value)} className="rounded-sm font-mono" data-testid="corp-business-fee-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Müşteriden Düşülecek Tutar</Label>
                    <Select value={form.customer_deduction} onValueChange={(v) => set("customer_deduction", v)}>
                      <SelectTrigger className="rounded-sm" data-testid="corp-deduction-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agreement">Anlaşma tutarı (örn. 300 − 100 = 200₺ müşteriden)</SelectItem>
                        <SelectItem value="tariff">Tarifedeki ilk saat bedeli (örn. 300 − 150 = 150₺ müşteriden)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-[11px] text-orange-800">Kapsanan süre içinde çıkan araç müşteriden 0₺ öder; aşan sürede kalan tutar müşteriden tahsil edilir. İşletmeye yazılan tutar aylık hesap özetinde toplanır.</div>
                </div>
              )}

              {!isPlates && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Rozet Rengi</Label>
                    <Select value={form.badge_color} onValueChange={(v) => set("badge_color", v)}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(BADGE_COLORS).map((c) => <SelectItem key={c} value={c}>{COLOR_LABELS[c]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-3 pb-1">
                    <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="corp-active-switch" />
                    <Label>{form.active ? "Anlaşma aktif" : "Pasif (menülerde görünmez)"}</Label>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><Label>İletişim Email</Label><Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="rounded-sm" /></div>
                <div><Label>İletişim Telefon</Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="rounded-sm" /></div>
              </div>
              <div><Label>Fatura Email</Label><Input type="email" value={form.billing_email} onChange={(e) => set("billing_email", e.target.value)} className="rounded-sm" /></div>

              {isPlates && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>İndirim (%)</Label><Input type="number" min="0" max="100" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className="rounded-sm" data-testid="corp-discount-input" /></div>
                    <div><Label>Aylık Limit (₺)</Label><Input type="number" step="0.01" value={form.monthly_credit_limit} onChange={(e) => set("monthly_credit_limit", e.target.value)} className="rounded-sm" /></div>
                  </div>
                  <div>
                    <Label>Plakalar (virgülle ayır)</Label>
                    <Textarea value={form.plates} onChange={(e) => set("plates", e.target.value)} className="rounded-sm font-plate uppercase" placeholder="34ABC123, 06XYZ99" data-testid="corp-plates-input" />
                  </div>
                </>
              )}
              <div><Label>Notlar</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm" /></div>
              <Button data-testid="corp-save-btn" onClick={save} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
                Kaydet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>İşletme</TableHead>
                <TableHead>Anlaşma</TableHead>
                <TableHead>Detay</TableHead>
                <TableHead>İletişim</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Kurumsal hesap yok</TableCell></TableRow>}
              {items.map((it) => {
                const type = it.agreement_type || "plates";
                const color = BADGE_COLORS[it.badge_color] || BADGE_COLORS.violet;
                return (
                  <TableRow key={it.id} data-testid={`corp-row-${it.id}`} className={it.active === false ? "opacity-50" : ""}>
                    <TableCell>
                      <div className="flex items-center gap-2 font-semibold">
                        {type === "plates" ? <Buildings size={18} className="text-muted-foreground" /> : <Storefront size={18} weight="fill" className={color.split(" ")[0].replace("bg-", "text-")} />}
                        {it.name}
                        {it.active === false && <Badge variant="outline" className="rounded-sm text-[9px]">PASİF</Badge>}
                      </div>
                      {it.notes && <div className="text-xs text-muted-foreground mt-1">{it.notes}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border-transparent ${type === "plates" ? "bg-muted text-foreground" : color}`} data-testid={`corp-type-${it.id}`}>
                        {TYPE_LABELS[type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {type === "plates" && (
                        <div className="flex flex-wrap gap-1 items-center">
                          {(it.plates || []).slice(0, 4).map((p) => <Plate key={p}>{p}</Plate>)}
                          {(it.plates || []).length > 4 && <span className="text-xs text-muted-foreground">+{it.plates.length - 4}</span>}
                          <span className="text-xs text-muted-foreground ml-2">%{it.discount_percent || 0} indirim</span>
                        </div>
                      )}
                      {type === "quota" && (
                        <div className="font-mono" data-testid={`corp-quota-usage-${it.id}`}>
                          <span className={it.active_count >= it.quota_slots ? "text-red-600 font-bold" : "font-bold"}>{it.active_count ?? 0}</span>/{it.quota_slots} araç içeride
                          {it.active_count >= it.quota_slots && <span className="ml-2 text-[10px] uppercase text-red-600 font-semibold">Dolu</span>}
                          {it.monthly_fee > 0 && <div className="text-xs text-muted-foreground font-sans">Aylık {formatTL(it.monthly_fee)} abonelik</div>}
                        </div>
                      )}
                      {type === "first_hours" && (
                        <div>
                          İlk <b>{it.covered_hours}</b> sa işletmeden · <b>{formatTL(it.business_fee_per_vehicle || 0)}</b>/araç
                          <div className="text-xs text-muted-foreground">{it.active_count ?? 0} araç içeride · müşteriden {it.customer_deduction === "tariff" ? "tarife bedeli" : "anlaşma tutarı"} düşülür</div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{it.contact_email}</div>
                      <div className="text-muted-foreground">{it.contact_phone}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        {type === "first_hours" && (
                          <Button variant="outline" size="sm" onClick={() => setRecvFor(it)} className="rounded-sm text-xs border-orange-300 text-orange-800" data-testid={`corp-receivables-${it.id}`}>
                            <HandCoins size={14} /> İşletmeden Bekleyen
                          </Button>
                        )}
                        {type !== "plates" && (
                          <Button variant="outline" size="sm" onClick={() => setStmtFor(it)} className="rounded-sm text-xs" data-testid={`corp-statement-${it.id}`}>
                            <Receipt size={14} /> Hesap Özeti
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`corp-edit-${it.id}`}>Düzenle</Button>
                        <Button variant="ghost" size="sm" onClick={() => del(it.id)}><Trash size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <StatementDialog account={stmtFor} onClose={() => setStmtFor(null)} />
      <BusinessReceivablesDialog account={recvFor} onClose={() => { setRecvFor(null); load(); }} />
    </div>
  );
}

function StatementDialog({ account, onClose }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!account) return;
    setData(null);
    api.get(`/business-agreements/${account.id}/statement?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Hesap özeti yüklenemedi"));
  }, [account, month]);
  if (!account) return null;
  return (
    <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><Receipt size={18} /> {account.name} — Hesap Özeti</span>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm h-9 w-40" data-testid="corp-stmt-month" />
          </DialogTitle>
          <DialogDescription>Seçili aydaki etiketli araçlar, işletmeye fatura edilecek ve müşteriden tahsil edilen tutarlar.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-muted-foreground py-6 text-center">Yükleniyor…</div> : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center">
              <Stat label="Araç" value={data.vehicle_count} testid="corp-stmt-count" />
              <Stat label="İşletmeye Fatura" value={formatTL(data.business_total)} accent testid="corp-stmt-business" />
              <Stat label="Müşteriden" value={formatTL(data.customer_total)} testid="corp-stmt-customer" />
              <Stat label="Kontenjan Dışı" value={data.over_quota_count} testid="corp-stmt-over" />
            </div>
            <div className="max-h-[50vh] overflow-y-auto border border-border rounded-sm">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Plaka</TableHead><TableHead>Giriş</TableHead><TableHead>Süre</TableHead>
                  <TableHead className="text-right">Normal</TableHead><TableHead className="text-right">Müşteri</TableHead><TableHead className="text-right">İşletme</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Bu ay kayıt yok</TableCell></TableRow>}
                  {data.items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell><div className="flex items-center gap-1"><Plate>{it.plate}</Plate>{it.business_over_quota && <Badge className="rounded-sm bg-red-600 text-white text-[8px] border-transparent">DIŞI</Badge>}</div></TableCell>
                      <TableCell className="text-xs font-mono">{formatDateTime(it.entry_time)}</TableCell>
                      <TableCell className="text-xs font-mono">{it.duration_minutes != null ? formatDuration(it.duration_minutes) : "içeride"}</TableCell>
                      <TableCell className="text-right text-xs font-mono text-muted-foreground">{it.pre_business_fee != null ? formatTL(it.pre_business_fee) : "—"}</TableCell>
                      <TableCell className="text-right text-xs font-mono">{it.status === "closed" ? formatTL(it.fee) : "—"}</TableCell>
                      <TableCell className="text-right text-xs font-mono font-semibold text-violet-700">{formatTL(it.business_fee || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="p-3 bg-muted rounded-sm">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold font-mono ${accent ? "text-violet-700" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}
